import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    Bell, Building2, Briefcase, Key, Info, Save, CheckCircle, AlertTriangle, Send, ShieldAlert, Clock, CloudRain
} from 'lucide-react';

const AdminAlerts = () => {
    const [companies, setCompanies] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const [selectedProjectId, setSelectedProjectId] = useState('');
    const [loading, setLoading] = useState(true);
    const [saveLoading, setSaveLoading] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [saveError, setSaveError] = useState(null);

    const [projectData, setProjectData] = useState({
        telegram_bot_token: '',
        telegram_chat_id: '',
        whatsapp_webhook_url: '',
        whatsapp_token: '',
        notify_ehs_incidents: true,
        notify_intrusions: true,
        notify_shift_events: false,
        notify_weather_alerts: false
    });

    useEffect(() => {
        const fetchCompaniesAndProjects = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('token');
                const res = await axios.get('http://localhost:4000/api/superadmin/companies', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setCompanies(res.data);
            } catch (err) {
                console.error("Error al cargar jerarquía de clientes:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchCompaniesAndProjects();
    }, []);

    // Cuando cambia el proyecto seleccionado, cargar sus datos
    useEffect(() => {
        if (!selectedProjectId) {
            setProjectData({
                telegram_bot_token: '',
                telegram_chat_id: '',
                whatsapp_webhook_url: '',
                whatsapp_token: '',
                notify_ehs_incidents: true,
                notify_intrusions: true,
                notify_shift_events: false,
                notify_weather_alerts: false
            });
            return;
        }

        // Buscar el proyecto seleccionado en la estructura local
        const company = companies.find(c => c.id === parseInt(selectedCompanyId));
        if (company) {
            const project = company.projects.find(p => p.id === parseInt(selectedProjectId));
            if (project) {
                setProjectData({
                    telegram_bot_token: project.telegram_bot_token || '',
                    telegram_chat_id: project.telegram_chat_id || '',
                    whatsapp_webhook_url: project.whatsapp_webhook_url || '',
                    whatsapp_token: project.whatsapp_token || '',
                    notify_ehs_incidents: project.notify_ehs_incidents !== false,
                    notify_intrusions: project.notify_intrusions !== false,
                    notify_shift_events: project.notify_shift_events === true,
                    notify_weather_alerts: project.notify_weather_alerts === true
                });
            }
        }
    }, [selectedProjectId, selectedCompanyId, companies]);

    const handleSaveAlertSettings = async (e) => {
        e.preventDefault();
        if (!selectedProjectId) return;
        setSaveLoading(true);
        setSaveSuccess(false);
        setSaveError(null);

        try {
            const token = localStorage.getItem('token');
            const res = await axios.put(`http://localhost:4000/api/projects/${selectedProjectId}`, projectData, {
                headers: { 
                    Authorization: `Bearer ${token}`,
                    'x-project-id': selectedProjectId
                }
            });

            // Actualizar la lista local de empresas con la nueva configuración
            setCompanies(prevCompanies => prevCompanies.map(c => {
                if (c.id === parseInt(selectedCompanyId)) {
                    return {
                        ...c,
                        projects: c.projects.map(p => {
                            if (p.id === parseInt(selectedProjectId)) {
                                return { ...p, ...projectData };
                            }
                            return p;
                        })
                    };
                }
                return c;
            }));

            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (err) {
            console.error("Error al guardar configuración de alertas:", err);
            setSaveError(err.response?.data?.error || "Error al actualizar la configuración.");
        } finally {
            setSaveLoading(false);
        }
    };

    const selectedCompany = companies.find(c => c.id === parseInt(selectedCompanyId));
    const availableProjects = selectedCompany ? selectedCompany.projects : [];

    return (
        <div className="p-6 md:p-10 max-w-5xl mx-auto space-y-8">
            {/* ENCABEZADO */}
            <div>
                <h1 className="text-3xl font-black flex items-center gap-3 text-theme-text-primary tracking-tight">
                    <Bell className="text-blue-500 animate-swing" size={32} /> Integración de Alertas y Webhooks
                </h1>
                <p className="text-sm text-theme-text-secondary mt-1 max-w-2xl">
                    Modulo exclusivo de Super Administrador para configurar integraciones y notificaciones instantáneas de obras con plataformas externas como WhatsApp y Telegram.
                </p>
            </div>

            {/* CARD SELECTORES */}
            <div className="bg-theme-panel border border-theme-border rounded-3xl p-6 shadow-xl grid grid-cols-1 md:grid-cols-2 gap-6 relative overflow-hidden">
                <div className="absolute right-0 top-0 w-32 h-32 bg-blue-500/5 rounded-full blur-2xl"></div>
                <div className="space-y-2">
                    <label className="text-xs font-bold text-theme-text-secondary uppercase tracking-wider flex items-center gap-1.5">
                        <Building2 size={14} className="text-blue-400" /> Seleccionar Cliente / Empresa
                    </label>
                    <select
                        value={selectedCompanyId}
                        onChange={(e) => {
                            setSelectedCompanyId(e.target.value);
                            setSelectedProjectId('');
                        }}
                        disabled={loading}
                        className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-sm text-theme-text-primary focus:outline-none focus:border-blue-500/50"
                    >
                        <option value="">-- Elige una Empresa --</option>
                        {companies.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                </div>

                <div className="space-y-2">
                    <label className="text-xs font-bold text-theme-text-secondary uppercase tracking-wider flex items-center gap-1.5">
                        <Briefcase size={14} className="text-purple-400" /> Seleccionar Proyecto / Obra
                    </label>
                    <select
                        value={selectedProjectId}
                        onChange={(e) => setSelectedProjectId(e.target.value)}
                        disabled={loading || !selectedCompanyId}
                        className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-sm text-theme-text-primary focus:outline-none focus:border-purple-500/50 disabled:opacity-50"
                    >
                        <option value="">-- Elige un Proyecto --</option>
                        {availableProjects.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {loading && (
                <div className="bg-theme-panel border border-theme-border rounded-3xl p-12 text-center text-zinc-500 flex flex-col items-center justify-center gap-2">
                    <div className="h-8 w-8 border-4 border-white/20 border-t-blue-500 rounded-full animate-spin"></div>
                    <span className="text-xs">Cargando jerarquía de obras...</span>
                </div>
            )}

            {!loading && !selectedProjectId && (
                <div className="bg-theme-panel/40 border border-theme-border rounded-3xl p-12 text-center text-zinc-500 flex flex-col items-center justify-center gap-3">
                    <Info size={32} className="opacity-30 text-blue-400" />
                    <span className="text-sm italic">Selecciona un cliente y un proyecto arriba para gestionar su configuración de webhooks y alertas.</span>
                </div>
            )}

            {!loading && selectedProjectId && (
                <form onSubmit={handleSaveAlertSettings} className="space-y-6">
                    {/* FEEDBACK */}
                    {saveSuccess && (
                        <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm p-4 rounded-2xl flex items-center gap-3 animate-fade-in">
                            <CheckCircle size={20} className="shrink-0" />
                            <span>Configuración de alertas guardada exitosamente.</span>
                        </div>
                    )}
                    {saveError && (
                        <div className="bg-rose-500/10 border border-rose-500/30 text-rose-400 text-sm p-4 rounded-2xl flex items-center gap-3 animate-fade-in">
                            <AlertTriangle size={20} className="shrink-0" />
                            <span>{saveError}</span>
                        </div>
                    )}

                    {/* CANALES DE ALERTA */}
                    <div className="bg-theme-panel border border-theme-border rounded-3xl p-8 shadow-xl space-y-6">
                        <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-2xl flex items-start gap-3 text-xs text-blue-400 font-medium">
                            <Info size={18} className="shrink-0 mt-0.5" />
                            <div>
                                Configura webhooks y bot tokens específicos para este proyecto. El sistema enviará capturas y eventos de forma autónoma según las reglas de inteligencia artificial configuradas.
                            </div>
                        </div>

                        {/* TELEGRAM */}
                        <div className="space-y-4">
                            <h3 className="font-bold text-sm text-theme-text-primary border-b border-theme-border/50 pb-2 flex items-center gap-2">
                                <Send size={16} className="text-blue-400" /> Configuración de Telegram
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-theme-text-secondary uppercase tracking-wider">Bot Token de Telegram</label>
                                    <input 
                                        type="text" 
                                        value={projectData.telegram_bot_token} 
                                        onChange={(e) => setProjectData({ ...projectData, telegram_bot_token: e.target.value })} 
                                        className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-theme-text-primary focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all text-sm font-mono text-xs" 
                                        placeholder="Ej. 123456789:ABCdefGhI..." 
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-theme-text-secondary uppercase tracking-wider">Chat ID de Destino</label>
                                    <input 
                                        type="text" 
                                        value={projectData.telegram_chat_id} 
                                        onChange={(e) => setProjectData({ ...projectData, telegram_chat_id: e.target.value })} 
                                        className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-theme-text-primary focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all text-sm font-mono text-xs" 
                                        placeholder="Ej. -100123456789" 
                                    />
                                </div>
                            </div>
                        </div>

                        {/* WHATSAPP */}
                        <div className="space-y-4 pt-4">
                            <h3 className="font-bold text-sm text-theme-text-primary border-b border-theme-border/50 pb-2 flex items-center gap-2">
                                <Key size={16} className="text-emerald-400" /> Configuración de WhatsApp Webhook
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2 md:col-span-2">
                                    <label className="text-xs font-bold text-theme-text-secondary uppercase tracking-wider">Webhook URL (API Gateway)</label>
                                    <input 
                                        type="url" 
                                        value={projectData.whatsapp_webhook_url} 
                                        onChange={(e) => setProjectData({ ...projectData, whatsapp_webhook_url: e.target.value })} 
                                        className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-theme-text-primary focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all text-sm font-mono text-xs" 
                                        placeholder="https://api.gateway.whatsapp.com/v1/messages..." 
                                    />
                                </div>
                                <div className="space-y-2 md:col-span-2">
                                    <label className="text-xs font-bold text-theme-text-secondary uppercase tracking-wider">Token de Autorización (Bearer Token)</label>
                                    <input 
                                        type="password" 
                                        value={projectData.whatsapp_token} 
                                        onChange={(e) => setProjectData({ ...projectData, whatsapp_token: e.target.value })} 
                                        className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-theme-text-primary focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all text-sm" 
                                        placeholder="Token secreto de autenticación..." 
                                    />
                                </div>
                            </div>
                        </div>

                        {/* EVENTOS A NOTIFICAR */}
                        <div className="space-y-4 pt-4">
                            <h3 className="font-bold text-sm text-theme-text-primary border-b border-theme-border/50 pb-2">Eventos a Notificar</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <label className="flex items-center gap-3 bg-theme-bg/50 p-4 rounded-xl border border-theme-border hover:border-theme-border/80 cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        checked={projectData.notify_ehs_incidents} 
                                        onChange={(e) => setProjectData({ ...projectData, notify_ehs_incidents: e.target.checked })} 
                                        className="rounded bg-theme-bg border-theme-border text-blue-500 focus:ring-0 w-4 h-4" 
                                    />
                                    <div>
                                        <div className="text-xs font-bold text-theme-text-primary flex items-center gap-1.5"><ShieldAlert size={14} className="text-rose-450" /> Incidentes de Seguridad EHS</div>
                                        <div className="text-[10px] text-theme-text-secondary">Falta de equipo de protección (PPE) o accesos restringidos.</div>
                                    </div>
                                </label>

                                <label className="flex items-center gap-3 bg-theme-bg/50 p-4 rounded-xl border border-theme-border hover:border-theme-border/80 cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        checked={projectData.notify_intrusions} 
                                        onChange={(e) => setProjectData({ ...projectData, notify_intrusions: e.target.checked })} 
                                        className="rounded bg-theme-bg border-theme-border text-blue-500 focus:ring-0 w-4 h-4" 
                                    />
                                    <div>
                                        <div className="text-xs font-bold text-theme-text-primary flex items-center gap-1.5"><Bell size={14} className="text-indigo-400" /> Intrusiones Nocturnas</div>
                                        <div className="text-[10px] text-theme-text-secondary">Detección de siluetas humanas fuera de horario de obra.</div>
                                    </div>
                                </label>

                                <label className="flex items-center gap-3 bg-theme-bg/50 p-4 rounded-xl border border-theme-border hover:border-theme-border/80 cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        checked={projectData.notify_shift_events} 
                                        onChange={(e) => setProjectData({ ...projectData, notify_shift_events: e.target.checked })} 
                                        className="rounded bg-theme-bg border-theme-border text-blue-500 focus:ring-0 w-4 h-4" 
                                    />
                                    <div>
                                        <div className="text-xs font-bold text-theme-text-primary flex items-center gap-1.5"><Clock size={14} className="text-amber-400" /> Ingresos y Salidas de Obra</div>
                                        <div className="text-[10px] text-theme-text-secondary">Fichaje e ingreso de personal y subcontratistas.</div>
                                    </div>
                                </label>

                                <label className="flex items-center gap-3 bg-theme-bg/50 p-4 rounded-xl border border-theme-border hover:border-theme-border/80 cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        checked={projectData.notify_weather_alerts} 
                                        onChange={(e) => setProjectData({ ...projectData, notify_weather_alerts: e.target.checked })} 
                                        className="rounded bg-theme-bg border-theme-border text-blue-500 focus:ring-0 w-4 h-4" 
                                    />
                                    <div>
                                        <div className="text-xs font-bold text-theme-text-primary flex items-center gap-1.5"><CloudRain size={14} className="text-blue-400" /> Alertas Climáticas Extremas</div>
                                        <div className="text-[10px] text-theme-text-secondary">Monitoreo de vientos fuertes o lluvias en tiempo real.</div>
                                    </div>
                                </label>
                            </div>
                        </div>

                        {/* BOTÓN GUARDAR */}
                        <div className="pt-4 flex justify-end">
                            <button 
                                type="submit" 
                                disabled={saveLoading} 
                                className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:scale-[1.01] text-white font-bold py-3.5 px-8 rounded-xl transition-all shadow-lg flex items-center gap-2 group disabled:opacity-50 cursor-pointer"
                            >
                                {saveLoading ? (
                                    <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                ) : (
                                    <Save size={20} className="group-hover:scale-110 transition-transform" />
                                )}
                                Guardar Integración de Alertas
                            </button>
                        </div>
                    </div>
                </form>
            )}
        </div>
    );
};

export default AdminAlerts;
